import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { tierDisplayName } from '../utils/tierNames';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Flame, Home, Users, Video, MessageCircle, CreditCard, 
  Settings, LogOut, Menu, X, ChevronRight, Sparkles,
  Gift, ArrowRight, FolderOpen, Music,
  Image as ImageIcon, Package, Calendar, Coins, Clock, 
  Download, RefreshCw, Loader2, CheckCircle2, XCircle,
  Play, Sun, Moon, Sunrise, TrendingUp, Zap, Crown, Eye,
  Share2, Shuffle, AlertCircle, Wand2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import NotificationBell from '../components/NotificationBell';
import AppShell from '../components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Time-based greeting
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return { text: 'Good morning', icon: Sunrise, period: 'morning' };
  if (hour < 17) return { text: 'Good afternoon', icon: Sun, period: 'afternoon' };
  return { text: 'Good evening', icon: Moon, period: 'evening' };
};

// Oracle suggestions based on time
// Prominent Ask Delphi hero input (routes into the Ask Delphi Action Layer)
const DashboardAskInput = () => {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const go = (e) => {
    e.preventDefault();
    if (value.trim()) navigate('/delphi', { state: { prompt: value.trim() } });
  };
  return (
    <form onSubmit={go} className="max-w-2xl">
      <div className="flex items-center gap-2 p-1.5 rounded-2xl bg-zinc-900/80 border border-zinc-800 focus-within:border-primary/50 transition-colors">
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder='Tell Delphi what to do… e.g. "Create a YouTube Short about my product"'
          className="flex-1 bg-transparent px-3 py-2.5 text-sm sm:text-base text-white placeholder:text-zinc-500 outline-none min-w-0"
          data-testid="dashboard-ask-input"
        />
        <Button type="submit" size="sm" className="btn-oracle h-10 px-4 shrink-0" data-testid="dashboard-ask-submit">
          <Sparkles className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Ask Delphi</span>
        </Button>
      </div>
    </form>
  );
};

const getOracleSuggestion = (greeting) => {
  const suggestions = {
    morning: [
      "Start your day with fresh content. Create a motivational video to engage your audience.",
      "Morning is prime time for social media. Let's create something inspiring.",
      "Your audience is waking up. Perfect time for a breakfast-worthy reel."
    ],
    afternoon: [
      "Afternoon energy calls for action. How about a quick product showcase?",
      "Peak engagement hours! Let's create content that stops the scroll.",
      "Your creativity is flowing. Time to turn ideas into viral content."
    ],
    evening: [
      "Wind down with a storytelling video. Your audience loves evening content.",
      "Evening vibes call for chill content. Create something relaxed and engaging.",
      "Perfect time for reflective content. What story will you tell tonight?"
    ]
  };
  
  const periodSuggestions = suggestions[greeting.period] || suggestions.afternoon;
  return periodSuggestions[Math.floor(Math.random() * periodSuggestions.length)];
};

// Friendly status messages
const getStatusMessage = (status, type) => {
  const messages = {
    pending: 'Starting up...',
    queued: 'Waiting in line...',
    interpreting: 'Understanding your idea...',
    generating_script: 'Writing the script...',
    script_ready: 'Script ready!',
    rendering: 'Creating your video...',
    stitching: 'Putting it all together...',
    generating: type === 'image' ? 'Painting your vision...' : 'Composing your track...',
    processing: 'Processing...',
    ready: 'Ready!',
    failed: 'Something went wrong',
    error: 'Needs attention'
  };
  return messages[status] || 'Working on it...';
};

// Type icon mapping
const TypeIcon = ({ type, className = "w-5 h-5" }) => {
  const icons = {
    video: Video,
    image: ImageIcon,
    music: Music,
    enhancement: Wand2
  };
  const Icon = icons[type] || Video;
  return <Icon className={className} />;
};

// Status badge with friendly labels
const StatusBadge = ({ status, type }) => {
  const statusConfig = {
    pending: { color: 'bg-zinc-500/20 text-zinc-400', icon: Clock },
    queued: { color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
    interpreting: { color: 'bg-blue-500/20 text-blue-400', icon: Loader2, spin: true },
    generating_script: { color: 'bg-blue-500/20 text-blue-400', icon: Loader2, spin: true },
    script_ready: { color: 'bg-yellow-500/20 text-yellow-400', icon: CheckCircle2 },
    rendering: { color: 'bg-purple-500/20 text-purple-400', icon: Loader2, spin: true },
    stitching: { color: 'bg-purple-500/20 text-purple-400', icon: Loader2, spin: true },
    generating: { color: 'bg-indigo-500/20 text-indigo-400', icon: Loader2, spin: true },
    processing: { color: 'bg-blue-500/20 text-blue-400', icon: Loader2, spin: true },
    ready: { color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle2 },
    failed: { color: 'bg-red-500/20 text-red-400', icon: XCircle },
    error: { color: 'bg-red-500/20 text-red-400', icon: XCircle }
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className={`w-3 h-3 ${config.spin ? 'animate-spin' : ''}`} />
      {getStatusMessage(status, type)}
    </span>
  );
};

// Active Job Card
const ActiveJobCard = ({ job, onRetry, index = 0 }) => {
  const progressPercent = job.progress || 0;
  const isFailed = job.status === 'failed' || job.status === 'error';
  
  const typeColors = {
    video: 'from-purple-500/20 to-pink-500/10 border-purple-500/20',
    image: 'from-blue-500/20 to-cyan-500/10 border-blue-500/20',
    music: 'from-indigo-500/20 to-purple-500/10 border-indigo-500/20',
    enhancement: 'from-amber-500/20 to-orange-500/10 border-amber-500/20'
  };
  
  const typeIconColors = {
    video: 'bg-purple-500/20 text-purple-400',
    image: 'bg-blue-500/20 text-blue-400',
    music: 'bg-indigo-500/20 text-indigo-400',
    enhancement: 'bg-amber-500/20 text-amber-400'
  };

  return (
    <div 
      className={`p-4 rounded-xl border transition-all card-lift bg-gradient-to-br list-item-in ${typeColors[job.type] || typeColors.video}`}
      style={{ animationDelay: `${index * 50}ms` }}
      data-testid={`active-job-${job.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${typeIconColors[job.type] || typeIconColors.video}`}>
          {isFailed ? (
            <AlertCircle className="w-5 h-5" />
          ) : (
            <Loader2 className="w-5 h-5 animate-spin" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium text-zinc-500 uppercase">{job.type}</span>
            {job.platform && (
              <span className="text-xs text-zinc-600 capitalize">• {job.platform}</span>
            )}
          </div>
          <p className="font-medium text-sm text-white truncate">{job.title || 'Untitled'}</p>
          <p className="text-xs text-zinc-500 mt-0.5">{job.message || getStatusMessage(job.status, job.type)}</p>
          
          {!isFailed && (
            <div className="mt-2">
              <Progress value={progressPercent} className="h-1.5" />
            </div>
          )}
          
          {isFailed && onRetry && job.type === 'video' && (
            <Button
              size="sm"
              variant="outline"
              className="mt-2 h-7 text-xs border-red-500/30 text-red-400 hover:bg-red-500/10"
              onClick={() => onRetry(job.id)}
              data-testid={`retry-btn-${job.id}`}
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Retry
            </Button>
          )}
        </div>
        {!isFailed && <span className="text-xs text-zinc-500 font-medium">{progressPercent}%</span>}
      </div>
    </div>
  );
};

// Ready Content Card
const ReadyContentCard = ({ content, onDownload, onView, index = 0 }) => {
  const thumbnailUrl = content.thumbnail_url || content.media_url;
  const hasMedia = content.media_url;
  
  const platformBadges = {
    instagram: { label: 'IG', color: 'bg-pink-500/80' },
    tiktok: { label: 'TT', color: 'bg-zinc-800' },
    youtube_shorts: { label: 'YT', color: 'bg-red-500/80' },
    facebook: { label: 'FB', color: 'bg-blue-500/80' }
  };
  
  const typeBadgeColors = {
    video: 'bg-purple-500/80',
    image: 'bg-blue-500/80',
    music: 'bg-indigo-500/80'
  };

  return (
    <div 
      className="group relative overflow-hidden rounded-xl bg-zinc-900/50 border border-zinc-800/50 hover:border-zinc-700 transition-all card-lift animate-scaleIn"
      style={{ animationDelay: `${index * 40}ms` }}
      data-testid={`ready-content-${content.id}`}
    >
      {/* Thumbnail */}
      <div className="aspect-[9/16] relative overflow-hidden bg-zinc-800">
        {thumbnailUrl && content.type !== 'music' ? (
          <img 
            src={thumbnailUrl} 
            alt={content.title}
            className="w-full h-full object-cover media-zoom"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
            <TypeIcon type={content.type} className="w-8 h-8 text-zinc-600" />
          </div>
        )}
        
        {/* Badges */}
        <div className="absolute top-2 left-2 flex gap-1">
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium text-white ${typeBadgeColors[content.type] || 'bg-zinc-600'}`}>
            {content.type.charAt(0).toUpperCase() + content.type.slice(1)}
          </span>
          {content.platform && platformBadges[content.platform] && (
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium text-white ${platformBadges[content.platform].color}`}>
              {platformBadges[content.platform].label}
            </span>
          )}
        </div>
        
        {content.source === 'enhanced' && (
          <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded text-[10px] font-medium text-amber-300 bg-amber-500/30">
            Enhanced
          </span>
        )}
        
        {/* Hover overlay with actions */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-200">
          <div className="absolute bottom-0 left-0 right-0 p-3 space-y-2">
            <div className="flex gap-2">
              {hasMedia && (
                <Button
                  size="sm"
                  variant="secondary"
                  className="flex-1 h-8 text-xs bg-white/10 hover:bg-white/20 backdrop-blur-sm"
                  onClick={() => onView?.(content)}
                  data-testid={`view-btn-${content.id}`}
                >
                  <Eye className="w-3 h-3 mr-1" /> View
                </Button>
              )}
              {hasMedia && (
                <Button
                  size="sm"
                  variant="secondary"
                  className="flex-1 h-8 text-xs bg-white/10 hover:bg-white/20 backdrop-blur-sm"
                  onClick={() => onDownload?.(content)}
                  data-testid={`download-btn-${content.id}`}
                >
                  <Download className="w-3 h-3 mr-1" /> Save
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                className="flex-1 h-8 text-xs bg-white/10 hover:bg-white/20 backdrop-blur-sm"
                onClick={() => toast.info('Share feature coming soon!')}
              >
                <Share2 className="w-3 h-3 mr-1" /> Share
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="flex-1 h-8 text-xs bg-white/10 hover:bg-white/20 backdrop-blur-sm"
                onClick={() => toast.info('Remix feature coming soon!')}
              >
                <Shuffle className="w-3 h-3 mr-1" /> Remix
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Content info */}
      <div className="p-3">
        <p className="text-sm font-medium text-white truncate">{content.title || 'Untitled'}</p>
        <p className="text-xs text-zinc-500 mt-0.5">
          {content.created_at ? new Date(content.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
        </p>
      </div>
    </div>
  );
};

// Quick Action Card
const QuickActionCard = ({ icon: Icon, label, sublabel, href, gradient, primary }) => (
  <Link 
    to={href} 
    className={`group flex flex-col items-center justify-center p-5 sm:p-6 rounded-2xl border transition-all duration-200 card-lift btn-press ${
      primary 
        ? 'bg-gradient-to-br from-primary/10 via-orange-500/5 to-transparent border-primary/30 hover:border-primary/50 hover:from-primary/15' 
        : 'bg-zinc-900/50 border-zinc-800/50 hover:border-zinc-700 hover:bg-zinc-900/80'
    }`}
    data-testid={`quick-action-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl ${gradient} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-200`}>
      <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
    </div>
    <p className="font-semibold text-sm sm:text-base text-white">{label}</p>
    <p className="text-xs text-zinc-500 mt-0.5">{sublabel}</p>
  </Link>
);

// Insight Card
const InsightCard = ({ icon: Icon, title, value, subtitle, color, action }) => (
  <div className="p-4 bg-zinc-900/30 rounded-xl border border-zinc-800/50">
    <div className="flex items-start justify-between">
      <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      {action}
    </div>
    <p className="text-2xl font-bold mt-3 text-white">{value}</p>
    <p className="text-sm text-zinc-400">{title}</p>
    {subtitle && <p className="text-xs text-zinc-600 mt-1">{subtitle}</p>}
  </div>
);

// Skeleton loader for content cards
const ContentSkeleton = () => (
  <div className="aspect-[9/16] bg-zinc-800/50 rounded-xl animate-pulse" />
);

const Dashboard = () => {
  const { user, token, logout, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Dashboard data state
  const [activeJobs, setActiveJobs] = useState([]);
  const [recentReady, setRecentReady] = useState([]);
  const [failedJobs, setFailedJobs] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  const greeting = useMemo(() => getGreeting(), []);
  const oracleSuggestion = useMemo(() => getOracleSuggestion(greeting), [greeting]);

  // Check for payment success
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const paymentStatus = params.get('payment');

    if (sessionId && paymentStatus === 'success') {
      pollPaymentStatus(sessionId);
    }
  }, [location]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.info('Payment processing. Your tier will be updated shortly.');
      return;
    }

    try {
      const response = await axios.get(`${API}/subscriptions/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription is now active.');
        refreshUser();
        navigate('/dashboard', { replace: true });
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Payment status error:', error);
    }
  };

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async (isRefresh = false) => {
    if (!token) return;
    
    if (isRefresh) setRefreshing(true);
    
    try {
      const response = await axios.get(`${API}/dashboard/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      setActiveJobs(data.active_jobs || []);
      setRecentReady(data.recent_ready || []);
      setFailedJobs(data.failed_jobs || []);
      setMetrics(data.metrics || null);
      
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      // Fallback to empty state
      if (!activeJobs.length && !recentReady.length) {
        setActiveJobs([]);
        setRecentReady([]);
        setMetrics(null);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboardData();
    
    // Poll for active jobs every 10 seconds
    const interval = setInterval(() => fetchDashboardData(false), 10000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const handleRetry = async (jobId) => {
    try {
      await axios.post(`${API}/reels/${jobId}/retry`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Retrying job...');
      fetchDashboardData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to retry job');
    }
  };

  const handleDownload = async (content) => {
    const url = content.media_url;
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleView = (content) => {
    if (content.type === 'video') {
      navigate(`/library?video=${content.id}`);
    } else if (content.type === 'image') {
      navigate(`/library?image=${content.id}`);
    } else {
      navigate('/library');
    }
  };

  const GreetingIcon = greeting.icon;

  // Only genuinely active jobs count as working — failed jobs are shown separately
  const hasActiveWork = activeJobs.length > 0;

  return (
    <AppShell>
      <div className="p-5 sm:p-7 lg:p-10 max-w-7xl mx-auto">
          
          {/* Trial Banner (non-founders only) */}
          {!user?.is_founder && user?.subscription_status === 'trial_active' && (
            <div className="mb-6 p-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 flex items-center justify-between gap-4 animate-fadeInUp" data-testid="trial-banner">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-emerald-400 shrink-0" />
                <div>
                  <span className="text-sm font-medium text-emerald-400">Free Trial Active</span>
                  {user?.trial_end && (
                    <span className="text-xs text-zinc-500 ml-2">
                      Ends {new Date(user.trial_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>
              </div>
              <Link to="/billing">
                <Button size="sm" variant="outline" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs h-8">
                  Choose a Plan
                </Button>
              </Link>
            </div>
          )}

          {!user?.is_founder && user?.subscription_status === 'trial_expired' && (
            <div className="mb-6 p-4 rounded-2xl border border-amber-500/30 bg-amber-500/5 flex items-center justify-between gap-4 animate-fadeInUp" data-testid="trial-expired-banner">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-400 shrink-0" />
                <span className="text-sm font-medium text-amber-400">Trial ended — choose a plan to keep creating</span>
              </div>
              <Link to="/billing">
                <Button size="sm" className="btn-oracle text-xs h-8">
                  Choose Plan
                </Button>
              </Link>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════════════
              SECTION 1: Ask Delphi — the primary experience
          ═══════════════════════════════════════════════════════════════════════ */}
          <section className="mb-10 animate-fadeInUp" data-testid="welcome-section">
            <p className="text-[10px] text-primary/60 uppercase tracking-[0.2em] font-semibold mb-3">
              {greeting.text}, {user?.name?.split(' ')[0]}
            </p>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-5 tracking-tight">
              What would you like Delphi to accomplish?
            </h1>
            <DashboardAskInput />
            <div className="flex flex-wrap gap-2 mt-4 max-w-2xl">
              {["Create a YouTube Short", "Promote my business", "Research my competitors",
                "Create an AI influencer", "Plan next week's content", "Create an image"].map((s) => (
                <Link key={s} to="/delphi" state={{ prompt: s }}>
                  <button type="button"
                    className="px-3 py-1.5 rounded-full text-xs bg-zinc-900 border border-zinc-800 text-zinc-300 hover:border-primary/50 hover:text-white transition-colors"
                    data-testid={`hero-chip-${s.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                    {s}
                  </button>
                </Link>
              ))}
            </div>

            {/* One small optional suggestion (kept compact by design) */}
            <div className="mt-5 flex items-center gap-2 max-w-2xl text-sm text-zinc-400">
              <Sparkles className="w-4 h-4 text-primary shrink-0" />
              <span className="truncate">{oracleSuggestion}</span>
              <Link to="/delphi" state={{ prompt: oracleSuggestion }} className="shrink-0">
                <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="oracle-suggestion-cta">Do it</Button>
              </Link>
            </div>
          </section>

          {/* ═══════════════════════════════════════════════════════════════════════
              SECTION 2: Manual studios (secondary)
          ═══════════════════════════════════════════════════════════════════════ */}
          <section className="mb-10 animate-fadeInUp delay-100" data-testid="quick-actions-section">
            <details className="group">
              <summary className="cursor-pointer list-none flex items-center gap-2 text-sm font-semibold text-zinc-400 hover:text-zinc-200 transition-colors select-none" data-testid="manual-tools-toggle">
                <span className="inline-block transition-transform group-open:rotate-90">›</span>
                Manual tools — for hands-on control
              </summary>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mt-4">
              <QuickActionCard
                icon={Video}
                label="Create Video"
                sublabel="Reels & Shorts"
                href="/studio/videos"
                gradient="bg-gradient-to-br from-purple-500 to-pink-500"
              />
              <QuickActionCard
                icon={ImageIcon}
                label="Create Image"
                sublabel="AI visuals"
                href="/studio/images"
                gradient="bg-gradient-to-br from-blue-500 to-cyan-500"
              />
              <QuickActionCard
                icon={Music}
                label="Create Music"
                sublabel="AI tracks"
                href="/studio/music"
                gradient="bg-gradient-to-br from-indigo-500 to-purple-500"
              />
              <QuickActionCard
                icon={Package}
                label="Combine"
                sublabel="Full package"
                href="/studio/combine"
                gradient="bg-gradient-to-br from-primary to-orange-500"
              />
              <QuickActionCard
                icon={MessageCircle}
                label="Ask Delphi"
                sublabel="Just say it"
                href="/delphi"
                gradient="bg-gradient-to-br from-orange-500 to-red-500"
                primary
              />
            </div>
            </details>
          </section>

          {/* ═══════════════════════════════════════════════════════════════════════
              SECTION 3: Delphi is working (only genuinely active jobs; hidden when idle)
          ═══════════════════════════════════════════════════════════════════════ */}
          {(loading || hasActiveWork) && (
          <section className="mb-10 animate-fadeInUp delay-150" data-testid="active-work-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                Delphi is working
              </h2>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-zinc-500 hover:text-white"
                onClick={() => fetchDashboardData(true)}
                disabled={refreshing}
                data-testid="refresh-jobs-btn"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-24 bg-zinc-800/30 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {activeJobs.slice(0, 6).map((job, index) => (
                  <ActiveJobCard 
                    key={job.id} 
                    job={job} 
                    index={index}
                    onRetry={handleRetry}
                  />
                ))}
              </div>
            )}
          </section>
          )}

          {/* Recent failures (last 24h) — never mixed with active work */}
          {!loading && failedJobs.length > 0 && (
          <section className="mb-10 animate-fadeInUp delay-150" data-testid="failed-work-section">
            <h2 className="text-sm font-semibold text-zinc-400 flex items-center gap-2 mb-3">
              <XCircle className="w-4 h-4 text-red-400" />
              Didn't finish
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {failedJobs.slice(0, 3).map((job, index) => (
                <ActiveJobCard key={job.id} job={job} index={index} onRetry={handleRetry} />
              ))}
            </div>
          </section>
          )}

          {/* ═══════════════════════════════════════════════════════════════════════
              SECTION 4: Ready for you (Recent Content)
          ═══════════════════════════════════════════════════════════════════════ */}
          <section className="mb-10 animate-fadeInUp delay-200" data-testid="ready-content-section">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                Ready for you
              </h2>
              <Link to="/library" className="text-xs text-zinc-600 hover:text-primary transition-colors duration-150 flex items-center gap-1">
                Creative Vault <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            
            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <ContentSkeleton key={i} />
                ))}
              </div>
            ) : recentReady.length === 0 ? (
              <div className="text-center py-12 px-4 bg-zinc-900/30 rounded-2xl border border-zinc-800/50">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500/20 to-red-500/10 border border-orange-500/30 flex items-center justify-center mx-auto mb-4">
                  <Eye className="w-8 h-8 text-orange-400" />
                </div>
                <p className="text-zinc-200 mb-2 font-medium">No content ready yet</p>
                <p className="text-sm text-zinc-500 mb-5 max-w-md mx-auto">Just tell Delphi what you want — it plans and produces your first creation in one prompt: image, video, music, or a full social drop.</p>
                <Link to="/delphi" state={{ prompt: "Create a hyperrealistic product hero image of a minimalist white sneaker on a marble surface with soft golden-hour light." }}>
                  <Button className="btn-oracle" data-testid="create-first-content-btn">
                    <Sparkles className="w-4 h-4 mr-2" /> Ask Delphi <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
                {recentReady.map((content, index) => (
                  <ReadyContentCard
                    key={`${content.type}-${content.id}`}
                    content={content}
                    index={index}
                    onDownload={handleDownload}
                    onView={handleView}
                  />
                ))}
              </div>
            )}
          </section>

          {/* ═══════════════════════════════════════════════════════════════════════
              SECTION 5: Usage & stats (collapsed — optional)
          ═══════════════════════════════════════════════════════════════════════ */}
          <section className="mb-10 animate-fadeInUp delay-250" data-testid="insights-section">
            <details className="group">
              <summary className="cursor-pointer list-none flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-300 transition-colors mb-5 select-none" data-testid="usage-stats-toggle">
                <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                <TrendingUp className="w-4 h-4" />
                Usage & stats
              </summary>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* Credits Available */}
              <InsightCard
                icon={Coins}
                title="Credits Available"
                value={metrics?.credits?.is_unlimited ? 'Unlimited' : (metrics?.credits?.total ?? '—')}
                subtitle={
                  metrics?.credits?.is_unlimited 
                    ? 'Founder access' 
                    : metrics?.credits?.reset_date 
                      ? `Resets ${new Date(metrics.credits.reset_date).toLocaleDateString()}`
                      : null
                }
                color="bg-primary/20"
                action={
                  <Link to="/billing">
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-zinc-500 hover:text-primary">
                      Add More
                    </Button>
                  </Link>
                }
              />
              
              {/* Videos Created */}
              <InsightCard
                icon={Video}
                title="Videos Created"
                value={metrics?.videos_created ?? 0}
                subtitle="Total ready videos"
                color="bg-purple-500/20"
              />
              
              {/* Images Created */}
              <InsightCard
                icon={ImageIcon}
                title="Images Created"
                value={metrics?.images_created ?? 0}
                subtitle="Total ready images"
                color="bg-blue-500/20"
              />
              
              {/* AI Influencers */}
              <InsightCard
                icon={Users}
                title="AI Influencers"
                value={metrics?.influencers ?? 0}
                subtitle="Digital personas"
                color="bg-indigo-500/20"
                action={
                  <Link to="/influencers">
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-zinc-500 hover:text-primary">
                      Manage
                    </Button>
                  </Link>
                }
              />
            </div>
            
            {/* Additional metrics row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-3">
              {/* Music Created */}
              <InsightCard
                icon={Music}
                title="Music Created"
                value={metrics?.music_created ?? 0}
                subtitle="AI-generated tracks"
                color="bg-pink-500/20"
              />
              
              {/* Scheduled Posts */}
              <InsightCard
                icon={Calendar}
                title="Scheduled Posts"
                value={metrics?.scheduled_posts ?? 0}
                subtitle="Upcoming content"
                color="bg-amber-500/20"
                action={
                  <Link to="/scheduler">
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-zinc-500 hover:text-primary">
                      View
                    </Button>
                  </Link>
                }
              />
              
              {/* Growth Tip */}
              <div className="col-span-2 p-4 bg-gradient-to-br from-emerald-500/10 to-teal-500/5 rounded-xl border border-emerald-500/20">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <Zap className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">Growth Tip</p>
                    <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                      {metrics?.videos_created < 5 
                        ? "Create your first 5 videos to unlock advanced analytics and personalized tips."
                        : metrics?.scheduled_posts === 0
                          ? "Schedule your content in advance to maintain consistent engagement with your audience."
                          : "Try Premium Voice for stronger engagement on your next video."
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
            </details>
          </section>

          {/* ═══════════════════════════════════════════════════════════════════════
              Founder Badge (if applicable)
          ═══════════════════════════════════════════════════════════════════════ */}
          {user?.is_founder && (
            <section className="mb-8" data-testid="founder-section">
              <div className="p-4 sm:p-5 bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent rounded-2xl border border-amber-500/20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <Crown className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-amber-200">Founder Account</p>
                    <p className="text-sm text-amber-400/70">
                      Thank you for being an early believer. All premium features unlocked.
                    </p>
                  </div>
                </div>
              </div>
            </section>
          )}

        </div>
    </AppShell>
  );
};

export default Dashboard;
